import {
  Button,
  Chip,
  Grid,
  GridColumn,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { CURRENT_YEAR, ORDER_HISTORY_COLUMN_HEADERS } from "@/constants";
import { SortOrder } from "@/types/common";
import {
  MappedOrderStatus,
  Order,
  OrderList,
  OrderSortableColumnKeys,
} from "@/types/Order";
import { getOrdersBySoldToId } from "@/utils/OrderService/OrderApi";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { Pagination } from "./Pagination";
import { YearFilter } from "./YearFilter";

const ActionsComponent: FC<{ order: Order }> = ({ order }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locale = useSelector((state: any) => state.globalStates.locale);
  const history = useHistory();
  return (
    <Button
      onClick={() => {
        history.push(`/${locale}/orders/${order?.orderId}`);
      }}
      variant="subtle"
    >
      View
    </Button>
  );
};

export const OrderTable: FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userData = useSelector((store: any) => store?.commerce?.userData);
  const [orderList, setOrderList] = useState<OrderList>();
  const [orderError, setOrderError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeyMap, setPageKeyMap] = useState<Map<number, string>>(new Map());
  const [sortDirection, setSortDirection] = useState<SortOrder>(SortOrder.DESC);
  const [sortProperty, setSortProperty] = useState<OrderSortableColumnKeys>(
    OrderSortableColumnKeys.DatePlaced
  );
  const [totalPage, setTotalPage] = useState<number>(0);

  const PAGE_SIZE = 10;

  const [yearFilter, setYearFilter] = useState<string>(String(CURRENT_YEAR));
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(false);

  const userId = userData?.userId;
  const soldToId = userData?.soldToAddress?.organizationId;
  const orders = orderList?.orders || [];

  useEffect(() => {
    const fetchOrdersBySoldTo = async () => {
      try {
        setIsLoadingOrders(true);
        const pageStartKey = pageKeyMap.get(currentPage);
        const data = await getOrdersBySoldToId({
          userId,
          soldToId,
          sortOrder: sortDirection,
          sortBy: sortProperty,
          pageStartKey,
          pageSize: PAGE_SIZE,
          year: yearFilter
        });
        setOrderList(data);
        setPageKeyMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(currentPage + 1, data.pagination.nextPageKey || "");
          return newMap;
        });
        if (data.totalPages) {
          setTotalPage(data.totalPages);
        }
      } catch (error) {
        setOrderError((error as Error)?.message || "Failed to fetch orders.");
      } finally {
        setIsLoadingOrders(false);
      }
    };

    fetchOrdersBySoldTo();
    // excluding the dependency on `pageKeyMap` to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, soldToId, sortDirection, sortProperty, userId, yearFilter]);

  const getOrderStatus = (mappedStatus: MappedOrderStatus) => {
    switch (mappedStatus) {
      case MappedOrderStatus.Cancelled:
        return <Chip label={"Cancelled"} />;
      case MappedOrderStatus.Processing:
        return <Text sx={{ pl: "spacing-3" }}>{"Processing"}</Text>;
      case MappedOrderStatus.OnHold:
        return <Chip variant="warning" label={"On Hold"} />;
      default:
        return <Chip variant="success" label={"Shipped"} />;
    }
  };

  const handleSortClick = (property: OrderSortableColumnKeys) => {
    const newSortDirection =
      sortProperty === property && sortDirection === SortOrder.ASC
        ? SortOrder.DESC
        : SortOrder.ASC;
    setSortDirection(newSortDirection);
    setSortProperty(property);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const handleYearFilterChange = (year: Number) => {
    setYearFilter(String(year));
    setCurrentPage(1); // Reset to first page on year filter change
    setSortDirection(SortOrder.DESC); // Reset sort direction to default
    setSortProperty(OrderSortableColumnKeys.DatePlaced); // Reset sort property to default
  };

  return (
    <ThemeProvider>
      <Grid>
        <GridColumn sx={{ pt: "spacing-6" }}>
          <Text variant="h4">Orders</Text>
        </GridColumn>
        <GridColumn size={3} sx={{ pt: "spacing-6" }}>
          <YearFilter
            value={Number(yearFilter)}
            onChange={handleYearFilterChange}
          />
        </GridColumn>
        <GridColumn sx={{ pt: "spacing-6" }}>
          {isLoadingOrders ? (
            <LoadingSpinner size="xl" />
          ) : orderError ? (
            <Text sx={{ pt: "spacing-6", color: "color-system-red-700" }}>
              {orderError}
            </Text>
          ) : orders.length === 0 ? (
            <Text sx={{ padding: "spacing-6" }}>No orders found.</Text>
          ) : (
            <Grid>
              <GridColumn size={12}>
                <Table spacing="default">
                  <TableHeaderRow>
                    <TableRow>
                      <TableHeaderCell key="orderId">
                        {ORDER_HISTORY_COLUMN_HEADERS.ORDER_ID}
                      </TableHeaderCell>
                      <TableHeaderCell key="poNumber">
                        {ORDER_HISTORY_COLUMN_HEADERS.PO_NUMBER}
                      </TableHeaderCell>
                      <TableHeaderCell key="placedBy">
                        {ORDER_HISTORY_COLUMN_HEADERS.PLACED_BY}
                      </TableHeaderCell>
                      <TableHeaderCell key="doeses">
                        {ORDER_HISTORY_COLUMN_HEADERS.DOSES}
                      </TableHeaderCell>
                      <TableHeaderCell
                        key="datePlaced"
                        onClick={() =>
                          handleSortClick(OrderSortableColumnKeys.DatePlaced)
                        }
                        sortable={true}
                        sortKey={OrderSortableColumnKeys.DatePlaced}
                        sortDirection={
                          sortProperty === OrderSortableColumnKeys.DatePlaced
                            ? sortDirection
                            : undefined
                        }
                      >
                        {ORDER_HISTORY_COLUMN_HEADERS.DATE_PLACED}
                      </TableHeaderCell>
                      <TableHeaderCell
                        key="status"
                        onClick={() =>
                          handleSortClick(OrderSortableColumnKeys.MappedStatus)
                        }
                        sortable={true}
                        sortKey={OrderSortableColumnKeys.MappedStatus}
                        sortDirection={
                          sortProperty === OrderSortableColumnKeys.MappedStatus
                            ? sortDirection
                            : undefined
                        }
                      >
                        {ORDER_HISTORY_COLUMN_HEADERS.STATUS}
                      </TableHeaderCell>
                      <TableHeaderCell key="actions">
                        {ORDER_HISTORY_COLUMN_HEADERS.ACTIONS}
                      </TableHeaderCell>
                    </TableRow>
                  </TableHeaderRow>
                  <TableBody>
                    {orders.map((orderData) => {
                      const {
                        orderNumber,
                        purchaseOrderNumber,
                        totalQuantity,
                        orderCreatedDate,
                        mappedOrderStatus,
                        orderId
                      } = orderData?.order;
                      const { firstName, lastName } = orderData?.placedByUser;
                      return (
                        <TableRow key={orderId}>
                          <TableCell key="orderNumber">
                            {orderNumber || "--"}
                          </TableCell>
                          <TableCell key="poNumber">
                            {purchaseOrderNumber || "--"}
                          </TableCell>
                          <TableCell key="placedBy">{`${firstName ?? "-"} ${lastName ?? "-"}`}</TableCell>
                          <TableCell key="doeses">
                            {totalQuantity || "--"}
                          </TableCell>
                          <TableCell key="datePlaced">
                            {orderCreatedDate.split("T")[0] || "--"}
                          </TableCell>
                          <TableCell key="status">
                            {getOrderStatus(mappedOrderStatus)}
                          </TableCell>
                          <TableCell key="actions">
                            <ActionsComponent order={orderData?.order} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </GridColumn>
              <Grid sx={{ mt: "spacing-9" }}>
                <GridColumn push={[0, 3, 5]} size={2}>
                  <Pagination
                    page={currentPage}
                    count={totalPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </GridColumn>
              </Grid>
            </Grid>
          )}
        </GridColumn>
      </Grid>
    </ThemeProvider>
  );
};
